import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './AdminMapChat.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const CHAT_BASE = `${API_BASE}/multi-asset/admin/map-chat`;

function fmtUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** SQLite/ISO UTC → America/Chicago display with mandatory "CDT" suffix (ops reference). */
function formatCdt(raw) {
  if (raw == null || raw === '') return '—';
  let s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2} /.test(s)) s = `${s.replace(' ', 'T')}Z`;
  else if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return String(raw);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} CDT`;
}

/** Prefer live timestamps over a UTC-stamped default title like "Chat 2026-07-23 18:14". */
function chatListTitle(c) {
  if (c.kind === 'external_import') return c.title;
  if (c.title && !/^Chat \d{4}-\d{2}-\d{2}/.test(c.title)) return c.title;
  return `Chat ${formatCdt(c.lastMessageAt || c.createdAt)}`;
}

export default function AdminMapChat() {
  const [chats, setChats] = useState([]);
  const [claude, setClaude] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [chat, setChat] = useState(null);
  const [draft, setDraft] = useState('');
  const [includeContext, setIncludeContext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importTitle, setImportTitle] = useState('');
  const [importText, setImportText] = useState('');
  const threadRef = useRef(null);

  const loadList = useCallback(async () => {
    const { data } = await axios.get(CHAT_BASE, { headers: authHeaders() });
    setChats(data.chats || []);
    setClaude(data.claude || null);
    return data.chats || [];
  }, []);

  const loadChat = useCallback(async (id) => {
    if (!id) {
      setChat(null);
      return;
    }
    const { data } = await axios.get(`${CHAT_BASE}/${id}`, { headers: authHeaders() });
    setChat(data.chat);
  }, []);

  useEffect(() => {
    loadList().catch((e) => setError(e.response?.data?.error || e.message));
  }, [loadList]);

  useEffect(() => {
    loadChat(activeId).catch((e) => setError(e.response?.data?.error || e.message));
    setSuggestions([]);
  }, [activeId, loadChat]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [chat]);

  const newChat = async () => {
    setError(null);
    try {
      const { data } = await axios.post(CHAT_BASE, {}, { headers: authHeaders() });
      await loadList();
      setActiveId(data.chat.id);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!activeId || !draft.trim()) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const text = draft;
    setDraft('');
    try {
      const { data } = await axios.post(
        `${CHAT_BASE}/${activeId}/message`,
        { text, includeContext },
        { headers: authHeaders() }
      );
      setChat(data.chat);
      if (data.usage) setClaude(data.usage);
      await loadList();
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
      setDraft(text);
    } finally {
      setBusy(false);
    }
  };

  const doImport = async (e) => {
    e.preventDefault();
    if (!importText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${CHAT_BASE}/import`,
        { title: importTitle, transcript: importText, source: 'claude.ai' },
        { headers: authHeaders() }
      );
      setImportText('');
      setImportTitle('');
      setShowImport(false);
      await loadList();
      setActiveId(data.chat.id);
      setMessage('External conversation imported.');
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const extract = async (mode) => {
    if (!activeId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post(
        `${CHAT_BASE}/${activeId}/extract`,
        { mode },
        { headers: authHeaders() }
      );
      await loadChat(activeId);
      if (data.usage) setClaude(data.usage);
      setSuggestions(data.policySuggestions || []);
      const bits = [];
      if (data.review) bits.push(`MAP → Evening Review draft #${data.review.id} (${data.review.status})`);
      if (data.mapError) bits.push(`MAP extract issue: ${data.mapError}`);
      if ((data.policySuggestions || []).length) bits.push(`${data.policySuggestions.length} policy suggestion(s)`);
      setMessage(bits.length ? bits.join(' · ') : 'Nothing actionable found in this conversation.');
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const applySuggestions = async () => {
    if (!suggestions.length) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${CHAT_BASE}/apply-policies`,
        { suggestions },
        { headers: authHeaders() }
      );
      setMessage(`Applied ${data.applied?.length || 0} policy change(s). Refresh the policies panel to confirm.`);
      setSuggestions([]);
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this conversation? It stays in the DB for strategy review.')) return;
    setBusy(true);
    try {
      await axios.delete(`${CHAT_BASE}/${id}`, { headers: authHeaders() });
      if (activeId === id) setActiveId(null);
      await loadList();
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="ma-map-chat" className="ma-admin-card ma-mapchat-card">
      <h3>MAP Chat (Phase 2)</h3>
      <p className="ma-card-desc">
        Talk to Claude about the book right inside Omni — every message is logged for strategy refinement and counts
        against the same monthly Claude cap. Or paste a claude.ai conversation and extract a MAP draft / policy updates.
      </p>

      {claude && (
        <p className="field-hint">
          Claude: {claude.configured ? 'configured' : 'NOT configured'} · model {claude.model || '—'} · month spend{' '}
          {fmtUsd(claude.monthSpendUsd)} / {fmtUsd(claude.monthlyCapUsd)} · remaining {fmtUsd(claude.remainingUsd)}
        </p>
      )}

      {error && <div className="error-message">{error}</div>}
      {message && <div className="ma-admin-success">{message}</div>}

      <div className="ma-mapchat-layout">
        <aside className="ma-mapchat-list">
          <div className="ma-mapchat-list-actions">
            <button type="button" className="primary-btn" disabled={busy} onClick={newChat}>
              + New chat
            </button>
            <button type="button" className="secondary-btn" disabled={busy} onClick={() => setShowImport((v) => !v)}>
              Paste external
            </button>
          </div>
          {showImport && (
            <form onSubmit={doImport} className="ma-mapchat-import">
              <input
                type="text"
                placeholder="Title (e.g. Omni MAP 3 with Gio)"
                value={importTitle}
                onChange={(e) => setImportTitle(e.target.value)}
              />
              <textarea
                rows={6}
                placeholder="Paste the full claude.ai conversation here…"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              <button type="submit" className="primary-btn" disabled={busy || !importText.trim()}>
                Import conversation
              </button>
            </form>
          )}
          <ul>
            {chats.map((c) => (
              <li
                key={c.id}
                className={c.id === activeId ? 'active' : ''}
                onClick={() => setActiveId(c.id)}
              >
                <div className="ma-mapchat-list-title">
                  {c.kind === 'external_import' ? '📋 ' : '💬 '}
                  {chatListTitle(c)}
                </div>
                <div className="ma-mapchat-list-meta">
                  {c.messageCount || 0} msg · {formatCdt(c.lastMessageAt || c.createdAt)}
                  <button
                    type="button"
                    className="ma-mapchat-archive"
                    title="Archive"
                    onClick={(e) => {
                      e.stopPropagation();
                      archive(c.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
            {!chats.length && <li className="ma-mapchat-empty">No conversations yet.</li>}
          </ul>
        </aside>

        <div className="ma-mapchat-main">
          {!chat && <p className="ma-mapchat-placeholder">Select a conversation or start a new one.</p>}
          {chat && (
            <>
              <div className="ma-mapchat-thread" ref={threadRef}>
                {(chat.messages || []).map((m) => (
                  <div key={m.id} className={`ma-mapchat-msg role-${m.role}`}>
                    <div className="ma-mapchat-msg-role">
                      <span>{m.role}</span>
                      <span className="ma-mapchat-msg-time">{formatCdt(m.createdAt)}</span>
                      {m.costUsd != null && <span className="ma-mapchat-cost"> · ${Number(m.costUsd).toFixed(4)}</span>}
                    </div>
                    <pre className="ma-mapchat-msg-body">{m.content}</pre>
                  </div>
                ))}
                {!(chat.messages || []).length && (
                  <p className="ma-mapchat-placeholder">Send a message to start. Tip: check &ldquo;include live book&rdquo; to give Claude the current positions/equity.</p>
                )}
              </div>

              <form onSubmit={send} className="ma-mapchat-compose">
                <div className="ma-mapchat-compose-card">
                  <label className="ma-checkbox-label ma-mapchat-context">
                    <input
                      type="checkbox"
                      checked={includeContext}
                      onChange={(e) => setIncludeContext(e.target.checked)}
                    />
                    Include live book (equity, positions, day P&amp;L)
                  </label>
                  <textarea
                    className="ma-mapchat-input"
                    rows={15}
                    placeholder={'Ask Claude about the book, a hedge, sizing…\n\nEnter to send · Shift+Enter for a new line'}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    disabled={busy}
                  />
                  <div className="ma-mapchat-compose-footer">
                    <span className="ma-mapchat-compose-hint">Messages stay in this thread above · times shown in CDT</span>
                    <button type="submit" className="primary-btn ma-mapchat-send" disabled={busy || !draft.trim()}>
                      {busy ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </div>
              </form>

              <div className="ma-mapchat-extract">
                <span>Extract from this conversation:</span>
                <button type="button" className="secondary-btn" disabled={busy} onClick={() => extract('map')}>
                  → MAP draft
                </button>
                <button type="button" className="secondary-btn" disabled={busy} onClick={() => extract('policies')}>
                  → Policy updates
                </button>
                <button type="button" className="secondary-btn" disabled={busy} onClick={() => extract('both')}>
                  → Both
                </button>
              </div>

              {suggestions.length > 0 && (
                <div className="ma-mapchat-suggestions">
                  <h4>Suggested policy changes</h4>
                  <ul>
                    {suggestions.map((s, i) => (
                      <li key={`${s.policyKey}-${i}`}>
                        <code>{s.action}</code> <strong>{s.policyKey}</strong>
                        {s.title ? ` — ${s.title}` : ''}
                        {s.value !== undefined && (
                          <span className="ma-mapchat-sug-val"> {JSON.stringify(s.value)}</span>
                        )}
                        {s.notes && <div className="ma-mapchat-sug-notes">{s.notes}</div>}
                      </li>
                    ))}
                  </ul>
                  <div className="ma-mapchat-suggestion-actions">
                    <button type="button" className="primary-btn" disabled={busy} onClick={applySuggestions}>
                      Apply {suggestions.length} change(s)
                    </button>
                    <button type="button" className="secondary-btn" disabled={busy} onClick={() => setSuggestions([])}>
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
